import useImportBooks from "api/BookResource/useImportBooks";
import { ButtonLoader, TextField } from "components";
import { Form, Formik } from "formik";
import { Dialog } from "primereact/dialog";
import { ProgressBar } from "primereact/progressbar";
import React, { useState } from "react";
import useOverlayStore from "store/overlayStore";
import { activeCurrencies } from "utils";
import {
	BsCloudUpload,
	BsFileEarmarkExcel,
	BsDownload,
	BsCheckCircle,
	BsFileText,
	BsExclamationTriangle,
} from "react-icons/bs";
import { FiUpload } from "react-icons/fi";

const UploadBooks = () => {
	const { showUploadBooks, setShowUploadBooks } = useOverlayStore();
	const [importSuccess, setImportSuccess] = useState(false);
	const [downloadInfo, setDownloadInfo] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [currentStep, setCurrentStep] = useState(0);
	const [uploadProgress, setUploadProgress] = useState(0);

	const initialValues = {
		csv: null,
	};

	const { mutateAsync, isLoading } = useImportBooks();
	const handleImportBooks = async (data) => {
		try {
			setUploadProgress(30);
			const result = await mutateAsync(data);
			setUploadProgress(70);
			if (result?.downloadUrl) {
				setDownloadInfo(result);
				setImportSuccess(true);
				setCurrentStep(2);
				setUploadProgress(100);
			}
		} catch (error) {
			setUploadProgress(0);
			// Error handling is done in the useImportBooks hook
		}
	};

	const handleDownload = () => {
		if (downloadInfo?.downloadUrl) {
			const link = document.createElement("a");
			link.href = downloadInfo.downloadUrl;
			link.download = downloadInfo.filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	};

	const handleModalClose = () => {
		setShowUploadBooks(false);
		setImportSuccess(false);
		setDownloadInfo(null);
		setSelectedFile(null);
		setCurrentStep(0);
		setUploadProgress(0);
	};

	const handleFileSelect = (event, setFieldValue) => {
		const file = event.target.files[0];
		if (file) {
			setSelectedFile(file);
			setFieldValue("csv", file);
			setCurrentStep(1);
		}
	};

	const steps = [
		{ label: "Prepare", icon: <BsFileText /> },
		{ label: "Upload", icon: <FiUpload /> },
		{ label: "Complete", icon: <BsCheckCircle /> },
	];

	return (
		<>
			<Dialog
				visible={showUploadBooks}
				style={{ width: "90vw", maxWidth: "520px" }}
				header={
					<div className="bg-white p-8 pb-4 font-sans ">
						<div className="text-center mb-6">
							<h1 className="text-2xl font-light text-gray-900 mb-2">Import Books</h1>
							<p className="text-gray-500 text-sm">Upload your book data in 3 simple steps</p>
						</div>
						<div className="flex items-center justify-center space-x-8">
							{steps.map((step, index) => (
								<div key={index} className="flex items-center">
									<div
										className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${
											index <= currentStep ? "bg-akgreen text-white" : "bg-gray-200 text-gray-500"
										}`}
									>
										{index < currentStep ? <BsCheckCircle className="text-sm" /> : index + 1}
									</div>
									{index < steps.length - 1 && (
										<div
											className={`w-16 h-0.5 mx-4 transition-all duration-200 ${
												index < currentStep ? "bg-akgreen" : "bg-gray-200"
											}`}
										/>
									)}
								</div>
							))}
						</div>
					</div>
				}
				onHide={handleModalClose}
				position="center"
				draggable={false}
				contentClassName="p-0 bg-white"
				className="rounded-2xl shadow-2xl border-0 overflow-hidden"
				modal={true}
				closable={true}
			>
				<div className="px-6 py-2">
					{/* Step 1: Preparation Guide */}
					{currentStep === 0 && (
						<div className="px-2">
							<div className="text-center mb-6">
								<div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
									<BsFileText className="text-2xl text-gray-600" />
								</div>
								<h3 className="text-lg font-medium text-gray-900 mb-2">Required Excel Format</h3>
								<p className="text-gray-500 text-sm leading-relaxed max-w-sm mx-auto mb-4">
									Your Excel file must contain these exact column headers with the specified data
									formats.
								</p>

								<button
									className="inline-flex items-center px-8 py-3 bg-akgreen hover:bg-akgreener text-white font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
									onClick={() => setCurrentStep(1)}
								>
									<FiUpload className="mr-2" />
									Upload Your File
								</button>
								<p className="text-xs text-gray-500 mt-2">
									Or review the format requirements below
								</p>
							</div>

							<div className="bg-gray-50 rounded-xl p-4 mb-6">
								<h4 className="font-medium text-gray-900 mb-4 text-center">
									Column Specifications
								</h4>

								<div className="space-y-3 ">
									{[
										{ header: "Title", type: "Text", required: true, example: "The Great Book" },
										{ header: "Author", type: "Text", required: true, example: "John Doe" },
										{
											header: `${activeCurrencies.USD.label} Price`,
											type: "Numeric",
											required: true,
											example: "19.99",
										},
										{
											header: `${activeCurrencies.ZWL.label} Price`,
											type: "Numeric",
											required: false,
											example: "1500",
										},
										{
											header: "ISBN Number",
											type: "Text",
											required: false,
											example: "978-1234567890",
										},
										{
											header: "Publisher Email",
											type: "Email",
											required: true,
											example: "publisher@example.com",
										},
										{
											header: "Publisher Firstname",
											type: "Text",
											required: true,
											example: "Jane",
										},
										{
											header: "Publisher Lastname",
											type: "Text",
											required: true,
											example: "Smith",
										},
										{
											header: "Publishing Company",
											type: "Text",
											required: true,
											example: "ABC Publishing",
										},
										{ header: "Category", type: "Text", required: true, example: "Fiction" },
										{ header: "Collection", type: "Text", required: true, example: "Best Sellers" },
										{
											header: "Summary",
											type: "Text",
											required: false,
											example: "A captivating story...",
										},
									].map((field, index) => (
										<div
											key={index}
											className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200"
										>
											<div className="flex items-center space-x-3">
												<div className="flex items-center space-x-2">
													<span className="font-medium text-gray-900 text-sm">{field.header}</span>
													{field.required && (
														<span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full font-medium">
															Required
														</span>
													)}
												</div>
											</div>
											<div className="flex items-center space-x-3 text-right">
												<span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
													{field.type}
												</span>
												<span className="text-xs text-gray-500 font-mono">{field.example}</span>
											</div>
										</div>
									))}
								</div>
							</div>

							<div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
								<h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
									<BsExclamationTriangle className="text-amber-600" />
									Important Notes
								</h4>
								<div className="space-y-1 text-sm text-amber-800">
									<p>
										• <strong>Publisher Email</strong> should correspond to the Senior Publisher's
										account email
									</p>
									<p>
										• <strong>Required fields</strong> cannot be empty or the import will fail
									</p>
									<p>
										• <strong>Price values</strong> should be numeric (e.g., 19.99, 1500)
									</p>
									<p>
										• <strong>Email format</strong> must be valid email addresses
									</p>
								</div>
							</div>
						</div>
					)}

					{/* Step 2: File Upload */}
					{currentStep === 1 && (
						<div className="px-2">
							<div className="text-center mb-8">
								<div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
									<BsCloudUpload className="text-2xl text-gray-600" />
								</div>
								<h3 className="text-lg font-medium text-gray-900 mb-2">Upload Your File</h3>
								<p className="text-gray-500 text-sm leading-relaxed max-w-sm mx-auto">
									Select your Excel (.xlsx) or CSV file to begin the import process.
								</p>
							</div>

							<Formik
								initialValues={initialValues}
								onSubmit={(data) => handleImportBooks(data)}
								enableReinitialize={true}
								key={importSuccess ? "reset" : "initial"}
							>
								{({ setFieldValue }) => {
									return (
										<Form>
											{!selectedFile ? (
												<div className="mb-4">
													<div className="border-2 border-dashed border-gray-200 rounded-2xl px-8 py-6 text-center hover:border-akgreen/50 hover:bg-akgreen/5 transition-all duration-300 group">
														<div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-akgreen/10 transition-colors duration-300">
															<BsCloudUpload className="text-xl text-gray-400 group-hover:text-akgreen transition-colors duration-300" />
														</div>
														<p className="text-gray-600 mb-2 font-medium">Drop your file here</p>
														<p className="text-gray-400 text-sm mb-4">
															or click to browse your computer
														</p>

														<div className="flex items-center justify-center gap-2 mb-4">
															<span className="px-3 py-1 bg-akgreen/10 text-akgreen text-xs rounded-full font-medium">
																XLSX
															</span>
															<span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
																XLS
															</span>
															<span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
																CSV
															</span>
														</div>

														<TextField
															type="file"
															name="csv"
															accept=".xlsx,.xls,.csv"
															value={null}
															onChange={(event) => handleFileSelect(event, setFieldValue)}
															className="hidden"
														/>
														<button
															type="button"
															className="inline-flex items-center px-6 py-3 bg-akgreen hover:bg-akgreener text-white font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
															onClick={() => document.querySelector('input[type="file"]').click()}
														>
															<FiUpload className="mr-2" />
															Choose File
														</button>
													</div>
												</div>
											) : (
												<div className="mb-8">
													<div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
														<div className="flex items-center space-x-3">
															<div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
																<BsFileEarmarkExcel className="text-green-600" />
															</div>
															<div className="flex-1 min-w-0">
																<p className="text-sm font-medium text-green-900 truncate">
																	{selectedFile.name}
																</p>
																<p className="text-xs text-green-700">
																	{(selectedFile.size / 1024).toFixed(1)} KB • Ready to import
																</p>
															</div>
															<div className="w-2 h-2 bg-green-500 rounded-full"></div>
														</div>
													</div>

													{isLoading && (
														<div className="bg-gray-50 rounded-xl p-4 mb-6">
															<div className="flex items-center space-x-3 mb-3">
																<ButtonLoader size={16} />
																<p className="text-sm font-medium text-gray-700">
																	Processing your file...
																</p>
															</div>
															<ProgressBar
																value={uploadProgress}
																className="w-full"
																showValue={false}
															/>
														</div>
													)}

													<div className="flex space-x-3">
														<button
															type="button"
															className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors duration-200"
															onClick={() => {
																setSelectedFile(null);
																setCurrentStep(0);
																setFieldValue("csv", null);
															}}
															disabled={isLoading}
														>
															Change File
														</button>
														<button
															type="submit"
															className="flex-1 px-4 py-3 bg-akgreen hover:bg-akgreener text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
															disabled={isLoading}
														>
															{isLoading ? (
																<div className="flex items-center justify-center">
																	<ButtonLoader size={16} />
																	<span className="ml-2">Processing...</span>
																</div>
															) : (
																<div className="flex items-center justify-center">
																	<FiUpload className="mr-2" />
																	Import Books
																</div>
															)}
														</button>
													</div>
												</div>
											)}
										</Form>
									);
								}}
							</Formik>
						</div>
					)}

					{/* Step 3: Success */}
					{currentStep === 2 && importSuccess && (
						<div className="px-2">
							<div className="text-center mb-8">
								<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
									<BsCheckCircle className="text-2xl text-green-600" />
								</div>
								<h3 className="text-lg font-medium text-gray-900 mb-2">Import Successful!</h3>
								<p className="text-gray-500 text-sm leading-relaxed max-w-sm mx-auto">
									Your books have been processed successfully. Download the result file to review
									the import details.
								</p>
							</div>

							<div className="bg-gray-50 rounded-xl p-4 mb-8">
								<h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
									<BsFileEarmarkExcel className="text-akgreen" />
									Result File Contains
								</h4>
								<div className="space-y-2 text-sm text-gray-600">
									<div className="flex items-center gap-2">
										<div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
										<span>Successfully imported book records</span>
									</div>
									<div className="flex items-center gap-2">
										<div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
										<span>Failed records with error messages</span>
									</div>
									<div className="flex items-center gap-2">
										<div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
										<span>Detailed status for each row</span>
									</div>
								</div>
							</div>

							<div className="flex space-x-3">
								<button
									onClick={handleDownload}
									className="flex-1 px-4 py-3 bg-akgreen hover:bg-akgreener text-white font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-2"
								>
									<BsDownload />
									Download Result
								</button>
								<button
									className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors duration-200"
									onClick={() => {
										setCurrentStep(0);
										setImportSuccess(false);
										setDownloadInfo(null);
										setSelectedFile(null);
										setUploadProgress(0);
									}}
								>
									Import More
								</button>
							</div>
						</div>
					)}

					{/* Navigation */}
					{currentStep < 2 && (
						<div className="px-2">
							<div className="flex justify-between pt-4 mt-2 border-t border-gray-100">
								<button
									className="px-6 py-3 text-gray-500 hover:text-gray-700 font-medium transition-colors duration-200"
									onClick={
										currentStep === 0 ? handleModalClose : () => setCurrentStep(currentStep - 1)
									}
								>
									{currentStep === 0 ? "Cancel" : "Previous Step"}
								</button>
								{currentStep === 0 && (
									<button
										className="px-6 py-3 bg-akgreen hover:bg-akgreener text-white font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
										onClick={() => setCurrentStep(1)}
									>
										Next Step
									</button>
								)}
							</div>
						</div>
					)}
				</div>
			</Dialog>
		</>
	);
};

export default UploadBooks;
